import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Palette, Plus, Sparkles } from 'lucide-react';
import { ROUTES } from '@/config/constants';
import { CreateCommunityModal } from '@/features/communities/components/CreateCommunityModal';

export function ToolkitMenu({ buttonClassName = 'h-12 w-12', iconSize = 28 }: { buttonClassName?: string; iconSize?: number }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setMenuOpen((v) => !v)}
        aria-label="WINR toolkit"
        aria-expanded={menuOpen}
        className={`flex items-center justify-center text-white/70 transition-all hover:scale-110 hover:text-white ${buttonClassName}`}
      >
        <svg
          viewBox="0 0 24 24"
          width={iconSize}
          height={iconSize}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transition-transform duration-200 ${menuOpen ? 'rotate-180' : ''}`}
        >
          <path d="M6 5l6 6 6-6M6 13l6 6 6-6" />
        </svg>
      </button>

      {menuOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-0 z-20 mt-2.5 w-72 origin-top-right animate-fade-in-up overflow-hidden rounded-2xl border border-white/10 bg-[linear-gradient(165deg,rgba(44,44,58,0.1),rgba(15,15,22,0.08))] p-2 shadow-[0_24px_60px_rgba(0,0,0,0.5)] backdrop-blur-2xl">
            <MenuRow
              icon={<Plus size={15} strokeWidth={2.5} />}
              tint="rounded-full bg-accent text-white shadow-[0_0_18px_rgba(84,185,254,0.35)]"
              title="Create a community"
              desc="Launch your own space"
              onClick={() => {
                setMenuOpen(false);
                setCreateOpen(true);
              }}
            />
            <MenuRow
              icon={<Palette size={15} strokeWidth={2.2} />}
              tint="text-accent bg-accent/15"
              title="WINR Studio"
              desc="Design your community & profile"
              onClick={() => {
                setMenuOpen(false);
                navigate(ROUTES.themeStudio);
              }}
            />
            <MenuRow
              icon={<Sparkles size={15} strokeWidth={2.2} />}
              tint="text-accent bg-accent/15"
              title="WINR AI"
              desc="Your creator copilot"
              onClick={() => setMenuOpen(false)}
            />
          </div>
        </>
      )}

      <CreateCommunityModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

function MenuRow({
  icon,
  tint,
  title,
  desc,
  onClick,
}: {
  icon: ReactNode;
  tint: string;
  title: string;
  desc: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2.5 text-left transition-all duration-150 hover:bg-white/[0.06] active:scale-[0.98]"
    >
      <span className={`grid h-8 w-8 flex-none place-items-center rounded-lg ${tint}`}>{icon}</span>
      <span className="min-w-0">
        <span className="block text-[13px] font-bold text-hi">{title}</span>
        <span className="block text-[11px] text-mid">{desc}</span>
      </span>
    </button>
  );
}
